/**
 * <park-choice> + <park-choice-option> — a single-select group of native
 * radio buttons, styled and sized as buttons (fix-stage-island-ux). Replaces
 * the `/varaa` island `<select>` (issue: island selection read as a form
 * afterthought, not the flow's first real decision).
 *
 * WHY NATIVE RADIOS, NOT `role="radio"` DIVS. Real `<input type="radio">`
 * gives arrow-key navigation between options, group semantics AT already
 * understands, and a submittable no-JS baseline — all for free, and all of
 * them the repo's no-JS gate (`check:ds-noscript`) and a11y rules require
 * anyway. A `role="radio"` div would have to reimplement every one of those
 * in JS.
 *
 * Isomorphic: pure `renderParkChoice()` shared by `ds/ParkChoice.astro`
 * (server, called with a plain `options` array — the real page has no author-
 * syntax children of its own) and the custom element (client, which parses
 * `<park-choice-option>` author-syntax children out of `this.slotHtml`,
 * mirroring `park-steps`'s `<park-step>` pattern — used by the Claude Design
 * mirror, not by the live page).
 *
 * No default selection is the DEFAULT: an option renders WITHOUT `checked`
 * unless it explicitly opts in (`checked: true` in the data, or the
 * `checked` attribute in author syntax). The island picker relies on that
 * default — the page script reads "no radio checked" as "no island chosen
 * yet". A control that legitimately needs a pre-selected default (the
 * day/month view switcher below) opts one option in explicitly; that is the
 * exception, not a second default.
 *
 * "Highly visible" selected state: the CSS (wc/css/park-choice.css) marks the
 * checked option with an inset shadow, a heavier border, bold text AND a
 * checkmark glyph — never colour alone (repo-wide a11y rule).
 *
 * Attributes: name (the radio group / form field name), legend (the group's
 * accessible name — rendered as a visually-hidden `<legend>`, same rationale
 * as the old select's `aria-label`: the enclosing `<park-stage>` header
 * already shows the visible title, so a second visible label would repeat
 * it), variant ("segmented" — see below).
 *
 * VARIANT "segmented" (anton/forrest#1392) — two (or more) options read as
 * ONE button split in half rather than several separate pills: a shared
 * outer border/radius on the row, a divider between segments, only the
 * OUTER corners rounded. Used by the day/month view switcher so it reads as
 * the same family as the (default, separate-pills) island choice while
 * being visually one control. CSS-only — the markup and behaviour are
 * identical to the default variant, just a class on the fieldset.
 *
 * @dsSpec design-system#FR-3 — general-purpose single-select button group; a
 *   recurring pattern any staged form field can reuse, not booking-specific.
 *   (Traceability: specs/design-system/traceability.md; the generated card
 *   carries this same ref in its @dsCard marker — anton/forrest#846.)
 */

import { ParkElement, defineElement, esc, html } from "./base";

const TAG = "park-choice";
const OPTION_TAG = "park-choice-option";

export interface ParkChoiceProps {
	/** The `name` shared by every radio input in the group. */
	name?: string;
	/** Visually-hidden `<legend>` text — the group's accessible name. */
	legend?: string;
	/** "segmented" — see the module doc above. Omit for the default,
	 *  separate-pills look. */
	variant?: string;
}

export interface ParkChoiceOptionData {
	value?: string;
	label?: string;
	/** Pre-select this option. Default false — see "No default selection". */
	checked?: boolean;
}

/**
 * Pure render: (props, options) → inner HTML string. No DOM access, so it is
 * safe to call server-side (the .astro wrapper) and client-side (the
 * element). An option carries `checked` only when its data says so — see the
 * "no default selection is the default" note above.
 */
export function renderParkChoice(
	props: ParkChoiceProps,
	options: ParkChoiceOptionData[],
): string {
	const name = esc(props.name);
	const items = options
		.map(
			(o) =>
				html`<label class="choice-btn"><input type="radio" class="choice-input" name="${name}" value="${esc(o.value)}"${o.checked ? " checked" : ""}><span class="choice-face"><span class="choice-check" aria-hidden="true">✓</span><span class="choice-text">${esc(o.label)}</span></span></label>`,
		)
		.join("");
	const cls = props.variant
		? `choice-group choice-group--${esc(props.variant)}`
		: "choice-group";
	return html`<fieldset class="${cls}"><legend class="kg-sr-only">${esc(props.legend)}</legend><div class="choice-row">${items}</div></fieldset>`;
}

class ParkChoice extends ParkElement {
	static get observedAttributes(): string[] {
		return [];
	}

	protected render(): string {
		// Parse the author-syntax <park-choice-option value checked> children
		// (textContent = label) captured in slotHtml into the options array, then
		// render via the pure fn — mirrors park-steps's <park-step> parsing.
		const options: ParkChoiceOptionData[] = [];
		const tmp = document.createElement("div");
		tmp.innerHTML = this.slotHtml;
		for (const el of tmp.querySelectorAll(OPTION_TAG)) {
			options.push({
				value: el.getAttribute("value") ?? undefined,
				label: (el.textContent ?? "").trim(),
				checked: el.hasAttribute("checked"),
			});
		}
		return renderParkChoice(
			{
				name: this.attr("name"),
				legend: this.attr("legend"),
				variant: this.attr("variant") || undefined,
			},
			options,
		);
	}
}

// Author-syntax only: parsed by <park-choice>. Extends ParkElement (not
// HTMLElement directly) so the module stays SSR-import-safe via base.ts's
// guarded ElementBase.
class ParkChoiceOption extends ParkElement {
	static get observedAttributes(): string[] {
		return [];
	}
	protected render(): string {
		return this.slotHtml;
	}
}

defineElement(TAG, ParkChoice);
defineElement(OPTION_TAG, ParkChoiceOption);

export { ParkChoice, ParkChoiceOption };
